import { API_BASE_URL } from '$lib/config';
import { getAccessToken, refreshSession, setAccessToken } from './auth';

export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  retried = false
): Promise<T> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> | undefined)
    }
  });

  if (response.status === 401 && !retried && !endpoint.startsWith('/auth/')) {
    try {
      await refreshSession();
      return apiFetch<T>(endpoint, options, true);
    } catch {
      setAccessToken(null);
      throw new Error('Authentication required. Please log in again.');
    }
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? String((errorData as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  if (response.status === 204) return null as T;
  return (await response.json()) as T;
}

export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? 'An unexpected error occurred.');
}

// Shared ?q=&page=&... builder — every list endpoint follows this repo
// convention (see CLAUDE.md "List endpoints take ?q= for search plus
// pagination params").
export function toQuery(params: Record<string, string | number | boolean | undefined> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}
